import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { OrderTimeline } from "../OrderTimeline";

const NEXT = { processing: "shipped", shipped: "out_for_delivery", out_for_delivery: "delivered" };
const LABEL = { shipped: "Mark shipped", out_for_delivery: "Out for delivery", delivered: "Mark delivered" };
const FILTERS = [["", "All"], ["pending_payment", "Awaiting payment"], ["processing", "To ship"], ["shipped", "Shipped"], ["out_for_delivery", "Out for delivery"], ["delivered", "Delivered"], ["cancelled", "Cancelled"]];

const OrderRow = ({ o, onUpdated }) => {
  const [tracking, setTracking] = useState(o.tracking_number || "");
  const [busy, setBusy] = useState(false);
  const set = async (status) => {
    setBusy(true);
    try { await api.patch(`/admin/orders/${o.order_id}/status`, { status, tracking_number: tracking || undefined }); toast.success(`Order ${o.order_id} updated`); onUpdated(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const next = NEXT[o.status];
  return (
    <div className="bg-white rounded-2xl p-5 border border-[#C85A32]/10" data-testid={`admin-order-${o.order_id}`}>
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="font-display font-bold">{o.order_id}</p>
          <p className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString()} · {o.method.toUpperCase()}{o.simulated ? " (demo)" : ""} · {o.items.length} item(s)</p>
          <p className="text-sm mt-1">{o.shipping.name} · {o.shipping.phone} · {o.shipping.address}, {o.shipping.city}, {o.shipping.country}</p>
        </div>
        <div className="text-right">
          <p className="font-display text-xl font-extrabold">${o.total_usd.toFixed(2)}</p>
          <span data-testid={`admin-order-status-${o.order_id}`} className="text-xs font-bold uppercase px-3 py-1 rounded-full bg-stone-100">{o.status.replace(/_/g, " ")}</span>
        </div>
      </div>
      <OrderTimeline order={o} />
      {o.payment_status === "paid" && o.status !== "delivered" && o.status !== "cancelled" && (
        <div className="flex flex-wrap gap-2 mt-4 items-center">
          <input data-testid={`admin-tracking-input-${o.order_id}`} value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number (optional)" className="h-10 rounded-full border px-4 text-sm flex-1 min-w-[180px]" />
          {next && <button data-testid={`admin-advance-${o.order_id}`} disabled={busy} onClick={() => set(next)} className="h-10 px-5 rounded-full bg-[#1E5E3A] text-white text-sm font-semibold disabled:opacity-50">{LABEL[next]}</button>}
          <button data-testid={`admin-cancel-${o.order_id}`} disabled={busy} onClick={() => set("cancelled")} className="h-10 px-5 rounded-full border text-sm text-red-600 hover:bg-red-50">Cancel</button>
        </div>
      )}
    </div>
  );
};

export const AdminOrders = ({ onChange }) => {
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/orders", { params: { status: status || undefined, q: q || undefined } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  return (
    <div className="mt-6 space-y-4" data-testid="admin-orders">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([s, n]) => <button key={s || "all"} data-testid={`admin-order-filter-${s || "all"}`} onClick={() => setStatus(s)} className={`px-4 h-9 rounded-full text-sm border ${status === s ? "bg-[#1A1714] text-white" : "bg-white"}`}>{n}</button>)}
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="ml-auto"><input data-testid="admin-order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order or name" className="h-9 rounded-full border px-4 text-sm" /></form>
      </div>
      {orders === null ? <div className="h-32 rounded-2xl bg-stone-200 animate-pulse" /> : orders.length === 0 ? <p className="text-stone-500 text-sm" data-testid="admin-orders-empty">No orders found.</p> :
        orders.map((o) => <OrderRow key={o.order_id} o={o} onUpdated={() => { load(); onChange(); }} />)}
    </div>
  );
};
